import { Immutable } from "immer";
import React from "react";

import type { AwardColumnsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

type IProps = Immutable<{
    value: Value;
}>;

type Column = NonNullable<Value["columns"]>[number];
type Award = NonNullable<Column["award"]>;
type AwardImage = NonNullable<Award["award_image"]>;

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/award-columns-block.html``.
 *
 * Image: the legacy ``{% wagtail_image column.award.award_image
 * specs='height-150' %}`` expands (per ``lazy-wagtail-image.html``) to a plain
 * eager ``<img>`` under the puppeteer UA, with ``src`` = the single
 * ``height-150`` rendition and ``alt`` = the image TITLE (the ``wagtail_image``
 * tag uses ``original.title``, unlike ``{% image %}``'s ``default_alt_text``).
 * The rendition is serialized via ``extra_client_data_renditions`` on the
 * ``AwardSubBlock`` image field. The production-only lazysizes plumbing
 * (placeholder data-URI + ``<noscript>``) is not reproduced, matching the
 * FeaturesBlock / ColumnsCarouselBlock port convention.
 *
 * Link: ``award_link`` is the ``tsicommon_cms.Link`` snippet; the legacy
 * ``{% if award_link.link_external %}`` external-target check maps to the
 * serialized ``link_external`` field (raw URLField value, "" when the link
 * targets a page/relative/view -- same truthiness as the Django check).
 *
 * Content: legacy ``{% include_block column.award.content %}`` emits the
 * ``.rich-text``-wrapped HTML directly under ``<article>``. The thelabui
 * ``RichTextBlock`` adds one (style-free) wrapper ``<div>`` around that string;
 * no site SCSS targets ``.rich-text`` with a direct-child selector inside this
 * block (sealy/tempurpedic selectors are ``> a`` and descendant ``img`` only),
 * so the wrapper does not shift styling. It renders unconditionally, matching
 * the legacy unconditional ``include_block`` (an empty RichText serializes to
 * an empty-but-present ``.rich-text`` div).
 */
const renderAwardImage = (
    image: Immutable<AwardImage> | null | undefined,
): React.JSX.Element | null => {
    if (!image) {
        return null;
    }
    return (
        <img
            alt={image.title}
            src={image.renditions["height-150"]?.url ?? image.url}
            loading="lazy"
            decoding="async"
        />
    );
};

const AwardColumn = (
    props: Immutable<{ column: Column }>,
): React.JSX.Element => {
    const award = props.column.award;
    const link = award?.award_link;
    const img = renderAwardImage(award?.award_image);
    return (
        <article
            className={props.column.responsive_visibility?.visibility ?? ""}
        >
            {link ? (
                <a
                    href={link.url}
                    target={link.link_external ? "_blank" : undefined}
                    rel={link.link_external ? "noopener noreferrer" : undefined}
                >
                    {img}
                </a>
            ) : (
                img
            )}
            <RichTextBlock value={award?.content ?? ""} />
        </article>
    );
};

export const AwardColumnsBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const columns = v.columns ?? [];
    return (
        <section
            className={concatClassNames([
                "award-columns-block",
                v.background_style?.style ?? "",
                v.theme?.theme ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <div>
                {columns.map((column, i) => (
                    <AwardColumn key={i} column={column} />
                ))}
            </div>
        </section>
    );
};
